import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Lock, Trash2 } from "lucide-react";
import { devError } from "@/lib/logger";

function monthLabel(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return new Date(`${ym}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function defaultMonth() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);  // previous month
  return d.toISOString().slice(0, 7);
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-200 rounded bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const navigate = useNavigate();
  const [month, setMonth] = useState(defaultMonth());
  const daysInMonth = useMemo(() => {
    if (!month) return 30;
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  }, [month]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]); // {employee_id, lop_days}
  const [computed, setComputed] = useState(null);
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState("");
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  // Load active employees + banks
  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    Promise.all([
      api.get(`/companies/${activeId}/employees`, { params: { status: "Active" } }),
      api.get(`/companies/${activeId}/ledgers`, { params: { group: "Bank Accounts" } }),
    ])
      .then(([r1, r2]) => {
        if (!mounted) return;
        setEmployees(r1.data);
        setBanks(r2.data);
        if (r2.data.length && !bankId) setBankId(r2.data[0].id);
        setRows(r1.data.map((e) => ({ employee_id: e.id, lop_days: 0 })));
      })
      .catch((err) => devError("Load failed", err));
    return () => { mounted = false; };
  }, [activeId]);

  // Check whether the selected month is already finalized
  useEffect(() => {
    if (!activeId || !month) return;
    let mounted = true;
    api.get(`/companies/${activeId}/payruns/${month}`)
      .then((r) => { if (mounted) setExisting(r.data); })
      .catch(() => { if (mounted) setExisting(null); });
    return () => { mounted = false; };
  }, [activeId, month]);

  const setLop = (eid, val) => {
    setRows((rs) => rs.map((r) => r.employee_id === eid ? { ...r, lop_days: Number(val) || 0 } : r));
  };

  const onCompute = async () => {
    if (!rows.length) {
      toast.error("No employees in pay run");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/companies/${activeId}/payroll/compute`, { month, days_in_month: daysInMonth, rows });
      setComputed(r.data);
      toast.success(`Computed payslips for ${r.data.items.length} employees`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Compute failed");
    } finally { setLoading(false); }
  };

  const onFinalize = async () => {
    if (!computed) {
      toast.error("Run compute first");
      return;
    }
    if (!window.confirm(`Finalize pay run for ${monthLabel(month)}? This will post a Journal voucher and cannot be undone.`)) return;
    setPosting(true);
    try {
      const r = await api.post(`/companies/${activeId}/payroll/finalize`, {
        month, days_in_month: daysInMonth, bank_ledger_id: bankId, rows,
      });
      toast.success(`Pay run finalized — voucher ${r.data.voucher_no} posted`);
      setExisting(r.data);
      navigate(`/payroll/payslips?month=${month}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Finalize failed");
    } finally { setPosting(false); }
  };

  /**
   * Delete a finalized pay run. Cascades on the backend: removes payslips +
   * the underlying Journal voucher, then unlocks the month so the user can
   * re-compute and re-finalize with corrected numbers. This is the "edit"
   * mechanism — we don't allow in-place edits because pay-run posting is
   * multi-document (voucher + N payslips) and partial edits corrupt the
   * double-entry.
   */
  const onDelete = async () => {
    if (!existing) return;
    if (!window.confirm(
      `Delete the pay run for ${monthLabel(month)}?\n\n` +
      `This will remove:\n` +
      `  • Voucher ${existing.voucher_no}\n` +
      `  • ${existing.slip_count || "all"} payslips for this month\n\n` +
      `You can then re-compute and re-finalize with corrected numbers.`
    )) return;
    try {
      await api.delete(`/companies/${activeId}/payruns/${existing.id}`);
      toast.success(`Pay run for ${monthLabel(month)} deleted — you can re-post now.`);
      setExisting(null);
      setComputed(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const empById = Object.fromEntries(employees.map((e) => [e.id, e]));
  const totals = computed?.totals || {};

  return (
    <>
      <PageHeader title="Pay Run" subtitle={existing ? `${monthLabel(month)} — POSTED via voucher ${existing.voucher_no}` : `Process payroll for ${monthLabel(month)}`} />
      <div className="p-6 space-y-4" data-testid="payrun-page">
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border border-gray-200 rounded bg-white p-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setComputed(null); }} className="h-9" data-testid="payrun-month" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Days in month</Label>
            <Input type="number" min={28} max={31} value={daysInMonth} readOnly className="h-9 font-mono bg-gray-50" data-testid="payrun-days" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Bank A/c to credit</Label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger className="h-9" data-testid="payrun-bank"><SelectValue placeholder="Select bank ledger" /></SelectTrigger>
              <SelectContent>
                {banks.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`opt-bank-${b.id}`}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={onCompute} disabled={loading || !!existing} className="h-9 flex-1" data-testid="btn-compute"><Calculator className="w-4 h-4 mr-1.5" /> {loading ? "Computing…" : "Compute"}</Button>
            <Button onClick={onFinalize} disabled={posting || !computed || !!existing} variant="default" className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-finalize"><Lock className="w-4 h-4 mr-1.5" /> {posting ? "Posting…" : "Finalize"}</Button>
          </div>
        </div>

        {existing && (
          <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-800 flex items-center justify-between gap-3" data-testid="payrun-already-posted">
            <span>
              Pay run for <b>{monthLabel(month)}</b> is already finalized — voucher <span className="font-mono">{existing.voucher_no}</span> on {existing.salary_pay_date}.{" "}
              <a className="underline" href={`/payroll/payslips?month=${month}`}>View payslips →</a>
            </span>
            <Button
              onClick={onDelete}
              variant="outline"
              size="sm"
              className="h-7 text-red-700 border-red-300 hover:bg-red-50 shrink-0"
              data-testid="btn-delete-payrun"
              title="Delete this pay run — removes payslips + Journal voucher. Same month can then be re-finalized."
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Pay Run
            </Button>
          </div>
        )}

        {/* LOP entry table */}
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Code</th><th>Name</th><th>Designation</th><th className="text-right">Basic</th><th className="text-right">Gross (CTC)</th>
                <th className="text-right w-32">LOP days</th>
                {computed && <><th className="text-right">Computed Gross</th><th className="text-right">Deductions</th><th className="text-right">Net Pay</th></>}
              </tr>
            </thead>
            <tbody data-testid="payrun-rows">
              {rows.length === 0 ? (
                <tr><td colSpan={computed ? 9 : 6} className="text-center text-xs text-gray-400 py-6">No active employees. Add some on the Employees page.</td></tr>
              ) : rows.map((r) => {
                const e = empById[r.employee_id] || {};
                const slip = computed?.items.find((x) => x.employee_id === r.employee_id);
                const ctc = Number(e.basic) + Number(e.hra) + Number(e.allowance) + Number(e.bonus) + Number(e.other_earnings || 0);
                return (
                  <tr key={r.employee_id} data-testid={`payrun-row-${e.code}`}>
                    <td className="font-mono text-xs">{e.code}</td>
                    <td className="text-xs font-semibold">{e.name}</td>
                    <td className="text-xs">{e.designation || "—"}</td>
                    <td className="tx-num">{formatINR(e.basic)}</td>
                    <td className="tx-num">{formatINR(ctc)}</td>
                    <td className="text-right">
                      <Input type="number" min="0" max={daysInMonth} step="0.5" value={r.lop_days} onChange={(ev) => { setLop(r.employee_id, ev.target.value); setComputed(null); }} disabled={!!existing} className="h-7 w-20 ml-auto text-right font-mono" data-testid={`lop-${e.code}`} />
                    </td>
                    {computed && slip && (
                      <>
                        <td className="tx-num">{formatINR(slip.gross)}</td>
                        <td className="tx-num text-orange-700">{formatINR(slip.total_deductions)}</td>
                        <td className="tx-num font-semibold text-emerald-700" data-testid={`net-${e.code}`}>{formatINR(slip.net_pay)}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        {computed && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="payrun-totals">
            <Stat label="Gross" value={`₹ ${formatINR(totals.gross)}`} accent="text-blue-700" />
            <Stat label="PF" value={`₹ ${formatINR(totals.pf)}`} />
            <Stat label="ESI" value={`₹ ${formatINR(totals.esi)}`} />
            <Stat label="PT" value={`₹ ${formatINR(totals.pt)}`} />
            <Stat label="TDS" value={`₹ ${formatINR(totals.tds)}`} />
            <Stat label="Net Payable" value={`₹ ${formatINR(totals.net_pay)}`} accent="text-emerald-700" />
          </div>
        )}

        {/* Statutory rules */}
        <details className="text-[11px] text-gray-500 border border-gray-200 rounded p-3">
          <summary className="cursor-pointer text-xs text-gray-700">Statutory rules applied</summary>
          <ul className="mt-2 space-y-1 pl-4 list-disc">
            <li>Salary heads are pro-rated by (Days in month - LOP days) / Days in month.</li>
            <li>PF: Employee 12% of Basic (Basic capped at ₹15,000 → max ₹1,800). Disabled if PF not applicable on the employee.</li>
            <li>ESI: Employee 0.75% of Gross — applied only when Gross ≤ ₹21,000 and ESI is enabled.</li>
            <li>Professional Tax: ₹200/mo (Maharashtra default). Override per state in v2.</li>
            <li>TDS: Manual monthly value from Employee master, pro-rated for LOP.</li>
            <li>Finalize creates a Journal voucher: Dr Salary Expense; Cr PF/ESI/PT/TDS Payable; Cr selected Bank A/c.</li>
          </ul>
        </details>
      </div>
    </>
  );
}

export default function PayRun() {
  return <RequireCompany><Inner /></RequireCompany>;
}
